import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { widgetsAPI, Widget } from '../lib/api';
import {
  MessageSquare,
  Plus,
  Search,
  Globe,
  MoreVertical,
  Trash2,
  Edit,
  Code,
} from 'lucide-react';

export default function WidgetsPage() {
  const [widgets, setWidgets] = useState<Widget[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);

  useEffect(() => {
    loadWidgets();
  }, []);

  const loadWidgets = async () => {
    try {
      const data = await widgetsAPI.list();
      setWidgets(data.widgets);
    } catch (error) {
      console.error('Failed to load widgets:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this widget?')) return;

    try {
      await widgetsAPI.delete(id);
      setWidgets(widgets.filter((w) => w.id !== id));
      toast.success('Widget deleted');
    } catch (error) {
      console.error('Failed to delete widget:', error);
      toast.error('Failed to delete widget. Please try again.');
    }
    setOpenMenuId(null);
  };

  const filteredWidgets = widgets.filter((widget) =>
    widget.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (isLoading) {
    return (
      <div className="space-y-6" aria-busy="true" aria-live="polite">
        <span className="sr-only">Loading widgets…</span>
        {/* Header placeholder */}
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <div className="h-7 w-32 rounded bg-gray-200 animate-pulse motion-reduce:animate-none" />
            <div className="h-4 w-56 rounded bg-gray-100 animate-pulse motion-reduce:animate-none" />
          </div>
          <div className="h-10 w-32 rounded-lg bg-gray-200 animate-pulse motion-reduce:animate-none" />
        </div>
        {/* Search placeholder */}
        <div className="h-10 w-full rounded-lg bg-gray-100 animate-pulse motion-reduce:animate-none" />
        {/* Card grid placeholder */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className="bg-white rounded-xl border border-gray-200 overflow-hidden"
            >
              <div className="h-2 bg-gray-200" />
              <div className="p-5 space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-gray-200 animate-pulse motion-reduce:animate-none" />
                  <div className="space-y-2">
                    <div className="h-4 w-28 rounded bg-gray-200 animate-pulse motion-reduce:animate-none" />
                    <div className="h-3 w-16 rounded bg-gray-100 animate-pulse motion-reduce:animate-none" />
                  </div>
                </div>
                <div className="h-3 w-24 rounded bg-gray-100 animate-pulse motion-reduce:animate-none" />
                <div className="h-9 w-full rounded-lg bg-gray-100 animate-pulse motion-reduce:animate-none" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Widgets</h1>
          <p className="text-gray-600 mt-1">
            Manage your embeddable chat widgets
          </p>
        </div>
        <Link
          to="/widgets/new"
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          New Widget
        </Link>
      </div>

      {/* Search */}
      <div className="relative">
        <Search
          className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400"
          aria-hidden="true"
        />
        <input
          type="text"
          placeholder="Search widgets..."
          aria-label="Search widgets"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
        />
      </div>

      {/* Widgets List */}
      {filteredWidgets.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {searchQuery ? 'No widgets found' : 'No widgets yet'}
          </h3>
          <p className="text-gray-600 mb-6">
            {searchQuery
              ? 'Try a different search term'
              : 'Create your first chat widget to get started'}
          </p>
          {!searchQuery && (
            <Link
              to="/widgets/new"
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Create Widget
            </Link>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredWidgets.map((widget) => (
            <div
              key={widget.id}
              className="bg-white rounded-xl border border-gray-200 hover:border-gray-300 hover:shadow-sm transition-colors"
            >
              {/* Card Header: the widget's own configured brand color */}
              <div
                className="h-2 rounded-t-xl"
                style={{ backgroundColor: widget.theme_config.primaryColor }}
              />

              <div className="p-5">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div
                      className="w-10 h-10 rounded-lg flex items-center justify-center"
                      style={{
                        backgroundColor: widget.theme_config.primaryColor,
                      }}
                    >
                      <MessageSquare className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">
                        {widget.name}
                      </h3>
                      <span
                        className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-medium ${
                          widget.is_active
                            ? 'bg-green-50 text-green-700'
                            : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        <span
                          className={`w-1.5 h-1.5 rounded-full ${
                            widget.is_active ? 'bg-green-500' : 'bg-gray-400'
                          }`}
                          aria-hidden="true"
                        />
                        {widget.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>

                  {/* Menu */}
                  <div className="relative">
                    <button
                      onClick={() =>
                        setOpenMenuId(openMenuId === widget.id ? null : widget.id)
                      }
                      aria-label={`Actions for ${widget.name}`}
                      aria-haspopup="menu"
                      aria-expanded={openMenuId === widget.id}
                      className="p-1 rounded-lg text-gray-500 hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 transition-colors"
                    >
                      <MoreVertical className="w-5 h-5" aria-hidden="true" />
                    </button>

                    {openMenuId === widget.id && (
                      <>
                        <div
                          className="fixed inset-0 z-10"
                          onClick={() => setOpenMenuId(null)}
                        />
                        <div
                          role="menu"
                          className="absolute right-0 mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20"
                        >
                          <Link
                            to={`/widgets/${widget.id}`}
                            className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                            onClick={() => setOpenMenuId(null)}
                          >
                            <Edit className="w-4 h-4" />
                            Edit Widget
                          </Link>
                          <Link
                            to={`/widgets/${widget.id}?tab=embed`}
                            className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                            onClick={() => setOpenMenuId(null)}
                          >
                            <Code className="w-4 h-4" />
                            Get Embed Code
                          </Link>
                          <button
                            onClick={() => handleDelete(widget.id)}
                            className="flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 w-full"
                          >
                            <Trash2 className="w-4 h-4" />
                            Delete
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {widget.description && (
                  <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                    {widget.description}
                  </p>
                )}

                <div className="flex items-center gap-4 text-sm text-gray-500">
                  <div className="flex items-center gap-1">
                    <Globe className="w-4 h-4" />
                    {widget.domain_count || 0} domain
                    {(widget.domain_count || 0) !== 1 ? 's' : ''}
                  </div>
                </div>

                <Link
                  to={`/widgets/${widget.id}`}
                  className="mt-4 block w-full text-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Manage Widget
                </Link>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
